// 찜질방 방 전체: 장면 + 규칙 + 손님 29마리 + 내 카피바라. 처음 들어갈 때 만든다.
// 매 프레임: 지금 구역만 보이기, 카메라 쪽 벽 숨기기, 쟁반·주문·덥고 추운 효과, 씻기 바가지, 놀이터 기구, 때밀이 부스러기, TV·서울 야경.
// 프로토타입 jjimTick 3591–3634, tick 3973–3978. 바가지는 dipper.ts.
import * as THREE from 'three';
import type { Stage } from '../../engine/renderer';
import type { Capy } from '../../capy/agent';
import { pose } from '../../capy/pose';
import { bodyFxTick, setBreathVisible } from '../../capy/fx';
import { placeScratcher } from '../../items/hand';
import { tickPops } from '../../food/eating';
import { tickSteam } from '../quiet/baths';
import { rinseMat } from '../water';
import { dipperTick } from './dipper';
import { JJIM_TEXT as TX } from '../../text/en';
import { AREAS, PLAY } from './layout';
import { buildJjimScene, type JjimScene } from './scene';
import { seoulTick } from './seoul';
import { tvShow } from './tv';
import { asJjim, goSpot, jjimStep, jjimThink, orderTick, placeTray, populate, releaseSpot, setNow, type JjimCapy, type JjimRules } from './bots';

export interface JjimHooks {
  say(a: Capy, text: string, t: number): void;
  note(text: string): void;
  hint(text: string): void;
  onShop?(): void;
}

export interface JjimRoom {
  S: JjimScene;
  world: THREE.Group;
  R: JjimRules;
  player: JjimCapy;
  /** 방에 들어올 때: 메인 홀 남쪽에서 시작, 아직 안 씻음. */
  enter(): void;
  /** 방을 떠날 때: 자리·쟁반·데크 상태를 정리한다. */
  leave(): void;
  update(t: number, dt: number): void;
}

const CRUMB_MAT = new THREE.MeshStandardMaterial({ color: 0x8E8A84, roughness: 1 });
const vT = new THREE.Vector3(), vT2 = new THREE.Vector3(), m4 = new THREE.Matrix4();

export function createJjimRoom(scene: THREE.Object3D, stage: Stage, player: Capy, hooks: JjimHooks): JjimRoom {
  const S = buildJjimScene(scene), world = S.world, me = asJjim(player);
  let now = 0, tvFrame = 0;
  const R: JjimRules = {
    S, agents: [me], world, navNow: S.nav, later: [],
    say: (a, text) => hooks.say(a, text, now), note: hooks.note, hint: hooks.hint, onShop: () => hooks.onShop?.(),
  };
  populate(R);
  const ctx = { room: 'jjim' as const };
  const crumbs: { m: THREE.Mesh; vy: number; t0: number }[] = [];

  function tick(t: number, dt: number) {
    const A = R.agents, area = me.area;
    // 지금 있는 구역만 그린다
    for (const k of AREAS) S.groups[k].visible = k === area;
    A.forEach((a) => {
      const vis = a.area === area; a.c.root.visible = vis;
      if (!vis) a.ripple.visible = false;
      setBreathVisible(a, vis);
      if (a.tray) { a.tray.group.visible = vis; placeTray(a); }
      if (a.dish) a.dish.group.visible = vis;
      orderTick(R, a, t);
      const s = a.spot && !a.spotPending ? a.spot : null;
      bodyFxTick(a, t, dt, { hot: a.area === 'hwangto' || a.area === 'salt' || (!!s && s.kind === 'hot'), cold: a.area === 'ice' || (!!s && s.kind === 'cold') }, world);
    });
    me.dineView = !!(me.spot && me.spot.kind === 'dine' && !me.spotPending && (me.eating || me.dish || (me.foodQueue && me.foodQueue.length)));
    if (me.dineView) A.forEach((a) => { if (a !== me) a.c.root.visible = false; });
    S.spots.forEach((s2) => { if (s2.stream) s2.stream.visible = s2.occ.some((o) => o.washT && !(o as JjimCapy).spotPending); });
    A.forEach((a) => dipperTick(a, t, dt, !!(a.washT && a.spot && a.spot.pose === 'wash' && !a.spotPending && a.c.root.visible), world));
    rinseMat.uniforms.uT!.value = t;
    // 내 카피바라와 카메라 사이 벽은 숨긴다
    const cp = stage.camera.position;
    S.walls.forEach((w) => { if (w.area !== area) return; w.side.visible = !((cp.x - w.px) * w.nx + (cp.z - w.pz) * w.nz > 0); });
    if (area === 'hall') { tvFrame++; if (tvFrame % 2 === 0) tvShow(S.tv, t, stage.renderer, S.tvRT); seoulTick(t); }
    // 트램펄린·볼풀·미끄럼틀
    let dip = 0;
    A.forEach((a) => {
      const sp = a.spot && !a.spotPending ? a.spot : null;
      if (sp && sp.pose === 'tramp') {
        const ph = (t * 1.15 + a.seedP) % 1;
        a.bounceY = Math.abs(Math.sin(ph * Math.PI)) * 1.5 * (a.size || 1) + 0.5; a.flipK = (Math.floor(t * 1.15 + a.seedP) % 4 === 0) ? ph : 0;
        if (ph < 0.12 || ph > 0.88) dip = Math.max(dip, 0.18);
      } else if (sp && sp.pose === 'pit') a.bounceY = -0.35;
      else if (sp && sp.pose === 'slide' && a.slideT) {
        const p = (t - a.slideT) / 3.2, Sd = PLAY.slide;
        if (p < 0.4) { const u = p / 0.4; a.bounceY = u * 1.3; a.pos.set(Sd.x, 0, Sd.z0 - 0.75 + u * 0.75); a.targetFacing = 0; }
        else if (p < 0.55) { a.bounceY = 1.35; a.pos.set(Sd.x, 0, Sd.z0); }
        else if (p < 0.9) { const u = (p - 0.55) / 0.35; a.bounceY = 1.35 * (1 - u) + 0.1; a.pos.set(Sd.x, 0, Sd.z0 + (Sd.z1 - Sd.z0) * u); a.targetFacing = 0; }
        else if (p < 1) { a.bounceY = 0; a.pos.set(Sd.x, 0, Sd.z1 + 0.5); }
        else { a.slideT = 0; a.bounceY = 0; releaseSpot(a); a.mode = 'goto'; a.wander = new THREE.Vector3(Sd.x + 1.2, 0, Sd.z1 + 0.6); a.next = t + 1; if (a.isPlayer) hooks.note(TX.slideAgain); }
      } else if (!a.spot || a.spotPending) { a.bounceY = 0; a.flipK = 0; }
    });
    if (S.fx.trampMat) S.fx.trampMat.position.y = 0.52 - dip;
    if (S.fx.balls && area === 'hall') {
      const b = S.fx.balls, base = b.userData.base as THREE.Vector3[];
      const kids = A.filter((a) => a.spot && a.spot.pose === 'pit' && !a.spotPending);
      if (kids.length) {
        for (let k = 0; k < base.length; k += 3) {
          const v = base[k]!; let off = 0;
          for (const a of kids) { const d = Math.hypot(v.x - a.pos.x, v.z - a.pos.z); if (d < 0.9) off += (0.9 - d) * 0.25 * (0.5 + 0.5 * Math.sin(t * 6 + k)); }
          m4.makeTranslation(v.x, v.y + off, v.z); b.setMatrixAt(k, m4);
        }
        b.instanceMatrix.needsUpdate = true;
      }
    }
    // 다 씻었으면: 가려던 탕으로
    A.forEach((a) => {
      if (!a.jWash || a.washT) return;
      a.jWash = false; a.washed = true;
      if (a.spot && (a.spot.pose === 'wash' || a.spot.pose === 'shower')) releaseSpot(a);
      if (a.isPlayer) { if (a.after && a.after.occ.length < a.after.cap) { const s2 = a.after; a.after = null; goSpot(a, s2); hooks.note(TX.cleanIn); } else hooks.note(TX.cleanTubs); }
    });
    // 효자손과 때밀이 부스러기
    if (me.scratcher) placeScratcher(me.scratcher, me.c, me.scratchT, t);
    if (me.scrubT && me.mitt && me.c.root.visible) {
      me.crumbT = (me.crumbT || 0) - dt;
      if (me.crumbT <= 0) {
        me.crumbT = 0.28; me.c.root.updateMatrixWorld(true); me.c.paws[0]!.getWorldPosition(vT);
        const cm = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.07, 5), CRUMB_MAT); cm.position.copy(vT); cm.rotation.set(Math.random() * 3, Math.random() * 3, 0); world.add(cm); crumbs.push({ m: cm, vy: 0.4, t0: t });
      }
    }
    for (let k = crumbs.length - 1; k >= 0; k--) { const c2 = crumbs[k]!; c2.vy -= 4 * dt; c2.m.position.y = Math.max(0.01, c2.m.position.y + c2.vy * dt); if (t - c2.t0 > 1.6) { world.remove(c2.m); crumbs.splice(k, 1); } }
  }

  return {
    S, world, R, player: me,
    enter() {
      world.add(me.c.root, me.ripple);
      me.area = 'hall'; me.washed = false; me.mode = 'idle'; me.route = null; me.wander = null; me.spot = null; me.spotPending = false;
      me.pos.set(0, 0, 9.5); me.c.root.position.copy(me.pos); me.facing = me.targetFacing = 0; me.path = null;
    },
    leave() {
      releaseSpot(me);
      me.lying = false; me.freeLie = false; me.lieAmt = 0; me.rollAnim = null; me.roll = 0; me.order = null; me.sitUp = false; me.bounceY = 0; me.flipK = 0;
      me.scrubT = 0; me.elevY = 0; me.stair = null; me.deckMove = null; me.jhop = null; me.dineView = false; me.route = null; me.after = null; me.foodQueue = null;
      if (me.tray) { me.tray.group.parent?.remove(me.tray.group); me.tray = null; }
      me.c.root.rotation.set(0, me.facing, 0);
    },
    update(t, dt) {
      now = t; setNow(R, t);
      for (const a of R.agents) { jjimThink(R, a, t); pose(a, t, dt, jjimStep(R, a, t, dt), ctx); }
      for (let i = R.later.length - 1; i >= 0; i--) if (t >= R.later[i]!.at) { const job = R.later[i]!; R.later.splice(i, 1); job.fn(t); }
      tick(t, dt);
      tickSteam(S.steam, t);
      tickPops(t);
    },
  };
}
